<template>
  <div class="game">
    <b-loading :is-full-page="false" v-model="isLoading"></b-loading>

    <div
      v-if="tournamentId"
      class="game__tournament"
      @click="
        () => {
          if (!playerColor || (match && match.result))
            $router.push({
              path: `/tournaments/${this.tournamentId}`,
            });
        }
      "
    >
      <TournamentGame
        :tournamentId="tournamentId"
        :matchId="matchId"
        :match="match"
      />
    </div>

    <div v-if="!isLoading" class="game__container">
      <!-- LEFT: Players and Timers -->
      <div class="game__left">
        <!-- TOP PLAYER -->
        <TimerProgressBar
          :duration="~~match.duration"
          :bufferDuration="match.buffer_duration"
          :timeLeft="orientation === 'white' ? blackTimeLeft : whiteTimeLeft"
          :bufferLeft="
            orientation === 'white' ? blackBufferLeft : whiteBufferLeft
          "
          :increment="orientation === 'white' ? blackIncrement : whiteIncrement"
        />

        <div class="game__player">
          <PlayerCard
            :player="topPlayer"
            :color="orientation === 'black' ? 'white' : 'black'"
            :playerColor="playerColor"
            :result="match.result"
            :score="
              orientation === 'black'
                ? match.player_1_score
                : match.player_2_score
            "
            :status="opponentStatus"
          />
          <TimerDisplay
            :duration="orientation === 'black' ? whiteTimeLeft : blackTimeLeft"
            :run="turnColor !== orientation"
          />
        </div>

        <!-- Player message -->
        <p v-if="playerMessage" class="has-text-danger">{{ playerMessage }}</p>
        <p v-else>
          <b-tooltip label="Flip board" type="is-primary">
            <b-button
              type="is-primary is-outlined"
              icon-left="retweet"
              @click="orientation = orientation === 'white' ? 'black' : 'white'"
            />
          </b-tooltip>
        </p>

        <!-- BOTTOM PLAYER -->
        <div class="game__player">
          <TimerDisplay
            :duration="orientation === 'black' ? blackTimeLeft : whiteTimeLeft"
            :run="turnColor === orientation"
          />

          <PlayerCard
            :player="botPlayer"
            :color="orientation"
            :playerColor="playerColor"
            :result="match.result"
            :score="
              orientation === 'black'
                ? match.player_2_score
                : match.player_1_score
            "
          />
        </div>

        <TimerProgressBar
          :duration="~~match.duration"
          :bufferDuration="match.buffer_duration"
          :timeLeft="orientation === 'white' ? whiteTimeLeft : blackTimeLeft"
          :bufferLeft="
            orientation === 'white' ? whiteBufferLeft : blackBufferLeft
          "
          :increment="orientation === 'white' ? whiteIncrement : blackIncrement"
        />
      </div>

      <!-- MIDDLE: Board and Actions -->
      <div class="game__middle" v-if="match && match.createdAt">
        <PvAiChessboard
          v-if="isAiGame"
          :aiLevel="aiLevel"
          :playerColor="moveTime < 0 ? '' : playerColor"
          :pgn="currentPgn"
          :matchResult="match.result"
          :orientation="orientation"
          :audio="true"
          :showThreats="gameSettings && gameSettings.showThreats"
          :enablePremove="gameSettings && gameSettings.premove"
          :autoQueen="gameSettings && gameSettings.autoQueen"
          @onMove="handleMove"
          @gameOver="handleGameOver"
        />
        <PvPChessboard
          v-else
          :playerColor="moveTime < 0 ? '' : playerColor"
          :pgn="currentPgn"
          :matchResult="match.result"
          :orientation="orientation"
          :audio="true"
          :showThreats="gameSettings && gameSettings.showThreats"
          :enablePremove="gameSettings && gameSettings.premove"
          :autoQueen="gameSettings && gameSettings.autoQueen"
          @onMove="handleMove"
          @gameOver="handleGameOver"
        />

        <!-- Request Draw and Resign buttons -->
        <GameActions
          v-if="playerColor && !match.result"
          class="game__footer"
          :playerColor="playerColor"
          :matchId="matchId"
          :move="lastMove"
          :canAbort="
            this.abortDuration &&
            !match.commencedAt &&
            ((playerColor === 'white' && !moves.length) ||
              (playerColor === 'black' && moves.length === 1))
          "
          @onResign="handleResign"
          @onRequestDraw="handleRequestDraw"
          @onAcceptDraw="handleAcceptDraw"
          @onDenyDraw="handleDenyDraw"
          @onPlayerAbort="handlePlayerAbort"
        />
        <Promote :playerColor="turnColor" />
      </div>

      <!-- RIGHT: Move Table -->
      <div class="game__right">
        <div class="game__moves" ref="moves_div">
          <MovesTable
            :moves="moves"
            :match="match"
            :playerColor="playerColor"
            :blackToStart="blackToStart"
            @onLoadMove="loadMove"
            @scrollTo="scrollTo"
          />
        </div>

        <!-- Game Settings and Report -->
        <section
          v-if="getProfile"
          class="is-flex is-justify-content-flex-end my-5"
        >
          <button
            class="button is-dark is-outlined"
            @click="analyseGame"
            v-if="getProfile.isAdmin && match.result"
          >
            <b-icon icon="diagnoses"></b-icon>
            <span>Analyse Game</span>
          </button>

          <b-tooltip
            type="is-info"
            label="Download PGN"
            v-if="getProfile.isAdmin && match.result"
          >
            <b-button
              icon-left="file-download"
              class="button is-info is-outlined"
              @click="downloadPGN"
            ></b-button>
          </b-tooltip>
          <GameReport
            :profile="getProfile"
            :url="'/#' + this.$route.path"
            :meta="{ moveTime, firestoreTimeOffset, bindFirestore }"
          />
          <GameSettings :gameSettings="gameSettings" />
        </section>
      </div>
    </div>

    <!-- Show to 2 players after match has ended -->
    <div v-if="tournamentId && playerColor && match && match.result">
      <button
        class="button is-primary is-outlined"
        @click="$router.push({ path: `/tournaments/${tournamentId}` })"
      >
        GO TO TOURNAMENT LOBBY
      </button>
    </div>
    <div v-else-if="playerColor && match && match.result">
      <InGameNav roomId="lobby" :match="match" />
    </div>

    <!-- Game analysis by Engine -->
    <GameAnalysis :moves="moves" :match="match" ref="gameAnalysis" />

    <!-- Show to spectators -->
    <div v-if="match && match.startedAt" class="has-text-info mt-5">
      <span>
        {{ match.result ? "Replaying" : playerColor ? "" : "Spectating" }}
        Match
      </span>
      <span v-if="match.startedAt">
        {{ match.startedAt | asDateTime }}
      </span>
      <span v-if="match.endedAt">- {{ match.endedAt | asTime }}</span>
    </div>

    <!-- Room match -->
    <div v-if="match && gameSettings">
      <RoomSpectators
        :roomId="matchId"
        :match="match"
        :showSpectators="gameSettings.showSpectators"
      />
    </div>

    <!-- Show to admin -->
    <GameAdmin
      v-if="!isLoading"
      :match="match"
      :moves="moves"
      :moveIndex="moveIndex"
      :blackToStart="blackToStart"
    />

    <div class="app__dev">
      <p v-if="gameResult">Game Result: {{ gameResult }}</p>
      <p v-if="!bindFirestore">No BindFirestore</p>
      <p>Move Time {{ Number(moveTime).toFixed(1) }}s</p>
      <p>Time Offset {{ Number(firestoreTimeOffset).toFixed(1) }}s</p>
      <p v-if="whiteTimeOffset != null">
        White Time Offset {{ whiteTimeOffset.toFixed(1) }}s
      </p>
      <p v-if="blackTimeOffset != null">
        Black Time Offset {{ blackTimeOffset.toFixed(1) }}s
      </p>
    </div>

    <GameFocus
      v-if="playerColor && !isLoading && !match.result"
      :focusRef="matchRef.collection(`focuses-${playerColor}`)"
    />
  </div>
</template>

<script>
import PlayerCard from "@/components/players/PlayerCard.vue";
import MovesTable from "@/components/games/MovesTable.vue";
import GameRenderless from "@/components/games/GameRenderless.js";
import TimerDisplay from "@/components/TimerDisplay.vue";
import TimerProgressBar from "@/components/timers/TimerProgressBar.vue";
import PvPChessboard from "@/components/variants/PvPChessboard.vue";
import PvAiChessboard from "@/components/variants/PvAiChessboard.vue";
import TournamentGame from "@/components/tournaments/TournamentGame.vue";
import Promote from "@/components/games/Promote.vue";
import GameActions from "@/components/games/GameActions.vue";
import GameSettings from "@/components/games/GameSettings.vue";
import GameReport from "@/components/games/GameReport.vue";
import GameAdmin from "@/components/games/GameAdmin.vue";
import InGameNav from "@/components/games/InGameNav.vue";
import RoomSpectators from "@/components/rooms/RoomSpectators.vue";
import GameFocus from "@/components/games/GameFocus.vue";
import GameAnalysis from "@/components/admins/GameAnalysis";
import store from "@/store/store.js";
import { mapGetters, mapActions } from "vuex";
import { downloadFile, updatePageTitle } from "@/utils";
import { firestore } from "@/services/firebase";
import gameUrls from "@/mixins/gameUrls";
import { ONLINE_STATUS } from "@/constants";
import { getUserId } from "@/services/auth";

export default {
  name: "game",

  store: store,

  components: {
    GameAnalysis,
    PlayerCard,
    TimerDisplay,
    TimerProgressBar,
    PvAiChessboard,
    PvPChessboard,
    GameActions,
    MovesTable,
    Promote,
    InGameNav,
    TournamentGame,
    GameAdmin,
    GameSettings,
    GameReport,
    RoomSpectators,
    GameFocus,
  },

  mixins: [gameUrls],

  data() {
    return {
      gameSettings: {},
      tournamentId: null,
      orientation: "white",
    };
  },

  computed: {
    ...mapGetters(["getOnlinePlayers"]),

    opponentStatus() {
      if (!this.playerColor) return null;
      if (this.isAiGame) return null;
      if (!this.getOnlinePlayers || !this.topPlayer) return null;
      if (this.topPlayer.user_id === this.getUserId) return null;
      const op = this.getOnlinePlayers.find(
        (p) => p.user_id === this.topPlayer.user_id
      );
      if (op) return op.status;
      return ONLINE_STATUS.OFFLINE;
    },

    topPlayer() {
      return this.orientation === "black" ? this.whitePlayer : this.blackPlayer;
    },

    botPlayer() {
      return this.orientation === "white" ? this.whitePlayer : this.blackPlayer;
    },

    playerMessage() {
      if (this.isLoading) return null;
      if (!this.match.result && !this.moves.length && this.moveTime < 0) {
        return `Game will starts in ${Math.floor(-this.moveTime)}s`;
      }

      if (
        !this.match.result &&
        !this.match.commencedAt &&
        this.abortDuration > this.moveTime
      ) {
        return `${Math.floor(
          this.abortDuration - this.moveTime
        )}s to play the first move`;
      }

      if (
        !this.match.result &&
        !this.match.commencedAt &&
        !this.abortDuration &&
        30 > this.moveTime
      ) {
        return `${Math.floor(30 - this.moveTime)}s until timer auto-starts`;
      }

      if (
        this.playerColor &&
        this.match.commencedAt &&
        !this.match.result &&
        this.turnColor !== this.playerColor &&
        this.opponentStatus === ONLINE_STATUS.OFFLINE
      ) {
        return `If ${this.topPlayer.first_name} doesn't come back in time, you can claim a victory.`;
      }

      return null;
    },

    tabTitle() {
      let title = "";
      if (this.isLoading) title = "loading...";
      else if (this.match && this.match.result) {
        title = `${this.whitePlayer.first_name} vs. ${this.blackPlayer.first_name}`;
      } else if (this.playerColor) {
        if (this.playerColor === this.turnColor) {
          title = `Your turn vs. ${this.topPlayer.first_name}`;
        } else title = `Waiting for ${this.topPlayer.first_name}`;
      }
      return title;
    },
  },

  watch: {
    tabTitle: function () {
      updatePageTitle(this.tabTitle);
    },

    match: {
      immediate: true,
      handler(match) {
        if (!match || !match.match_id) return;

        if (match.tournamentId && match.tournamentId != this.tournamentId) {
          this.$router.push({
            path: this.getTournamentGameUrl(match.tournamentId, match.match_id),
            query: this.$route.query,
          });
          return;
        }
        if (this.playerColor) {
          this.orientation = this.playerColor;
          if (!match.result && !this.$store.getters.getCurrentMatch) {
            this.$store.commit("setCurrentMatch", match);
          }
        }
      },
    },
  },

  extends: GameRenderless,

  methods: {
    ...mapActions(["queryProfileAction"]),

    scrollTo(n) {
      const scrollHeight = (420 * (n - (n % 2) - 10)) / 22;
      // - console.log("scrollTo", n, scrollHeight);
      this.$nextTick(() => this.$refs.moves_div.scrollTo(0, scrollHeight));
    },

    analyseGame() {
      this.$refs.gameAnalysis.loadData();
    },

    downloadPGN() {
      // todo: Add more PGN tags in the headers

      const headers = [];
      headers.push("[Site 'chesssquad.com']");
      headers.push(
        `[White "${this.match.player1.last_name}, ${this.match.player1.first_name}"]`
      );
      headers.push(
        `[Black "${this.match.player2.last_name}, ${this.match.player2.first_name}"]`
      );
      headers.push("\n");
      headers.push(this.match.pgn);

      downloadFile(
        headers.join("\n"),
        this.match.match_id + ".pgn",
        "text/pgn;charset=utf-8;"
      );
    },
  },

  created() {
    this.matchId = this.$route.params.gameId.toLowerCase();
    this.tournamentId = this.$route.params.tournamentId;

    const userId = this.getUserId || getUserId();
    if (userId) {
      this.$bind(
        "gameSettings",
        firestore.collection("settings").doc("" + userId)
      );
    }
  },
};
</script>

<style scoped>
.game {
  min-width: 670px;
  min-height: 800px;
  position: relative;
  padding: 0;
}

.game >>> .cg-board-wrap {
  border: 1px solid black;
}

.game__tournament {
  cursor: pointer;
}

@media (min-width: 600px) {
  .game >>> .cg-board-wrap {
    width: 480px;
    height: 480px;
  }
}

@media (max-width: 1150px) {
  .game__container {
    flex-direction: column;
  }

  .game__left {
    flex-direction: row !important;
    /* padding: 0 !important; */
    height: auto !important;
  }

  .game__player {
    display: flex;
    align-items: center;
  }

  .game__footer {
    justify-content: space-evenly !important;
  }

  .game__right {
    height: auto !important;
  }
}

.game__container {
  display: flex;
  justify-content: space-evenly;
}

.game__left {
  /* flex: 0.3; */
  margin: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 0.5;
  min-width: 200px;
  height: 480px;
  /* border: 1px solid black; */
}

.game__player {
  position: relative;
}

.game__resultTop {
  position: absolute;
  top: 5px;
  right: 5px;
}

.game__resultBot {
  position: absolute;
  bottom: 5px;
  right: 5px;
}

.base-timer {
  margin: 10px;
  font-size: 3em;
}

.game__middle {
  /* flex: 0.5; */
  flex-direction: column;
  margin-top: 20px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.game__footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.game__right {
  flex: 0.5;
  margin: 20px;
  min-width: 200px;
}

.game__moves {
  border: 2px solid #b7dcff;
  background-color: aliceblue;
  align-items: center;
  width: auto;
  height: 480px;
  overflow: scroll;
  overflow-x: hidden;
}
</style>
